import React, { useState, forwardRef, useImperativeHandle } from 'react';
import { styled } from '@mui/material/styles';
import { IconButton, Dialog, DialogTitle, DialogContent, DialogActions, Button, Typography, TableContainer, Grid, Box } from '@mui/material';
import TableChartIcon from '@mui/icons-material/TableChart';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import { printBVTTablesToPDF, generateInvoicePDF, generateExcel, formatDate } from '../utilityFunctions';
import CloseIcon from '@mui/icons-material/Close';
const PREFIX = 'TablePopoutButton';

const classes = {
    copyButton: `${PREFIX}-copyButton`,
    excelButton: `${PREFIX}-excelButton`
};

// TODO jss-to-styled codemod: The Fragment root was replaced by div. Change the tag if needed.
const Root = styled('div')({
    [`& .${classes.copyButton}`]: {
        margin: '10px 0',
    },
    [`& .${classes.excelButton}`]: {
        backgroundColor: '#1e613b',
        color: 'white',
        margin: '10px 0px 10px 10px',
        '&:hover': {
            backgroundColor: '#1e613b',
        },
    }
});

const TablePopoutButton = forwardRef(({
    tableComponent: TableComponent,
    budgetData,
    invoiceData,
    title,
    source,
    selectedProject,
    selectedSnapshots,
    concurUrl,
    projectData,
    firstSnapshot,
    secondSnapshot,
    setFirstSnapshot,
    setSecondSnapshot,
    overUnderData,
    projectId,
}, ref) => {

    const [openDialog, setOpenDialog] = useState(false);

    const handleClickOpen = () => {
        if (selectedProject) {
            setOpenDialog(true);
        }
    };

    const handleClose = () => {
        setOpenDialog(false);
    };

    useImperativeHandle(ref, () => ({
        triggerClick: handleClickOpen,
    }));

    let tableDescription = '';
    let showButtons = null;
    let viewWidth = 'xxl';
    let viewHeight = 'xxl';
    let tableHeight = '85vh';
    let baselineSnapshot = null;
    let currentSnapshot = null;
    let penultimateSnapshot = null;

    switch (source) {
        case 'trade over/under':
            tableDescription = 'The tables below show total variance by cost code between the two selected snapshots.';
            viewWidth = 'xl';
            tableHeight = '70vh';
            break;
    case 'paymentInvoicing':
            showButtons = 'payment-invoicing';
            tableDescription = "This table displays the project's owner invoicing history from Viewpoint.";
            viewWidth = 'xxl';
            viewHeight = 'xxl';
            tableHeight = '60vh';
            break;
    case 'concurTable':
            viewWidth = 'xxl';
            viewHeight = 'xxl';
            tableHeight = '85vh';
            break;
    case 'keystyleTable':
            viewWidth = 'xxl';
            viewHeight = 'xxl';
            tableHeight = '85vh';
            break;
    case 'invoicePortal':
            viewWidth = 'xxl';
            viewHeight = 'xxl';
            tableHeight = '85vh';
            break;
    case 'bvtTable':
            viewWidth = 'xl';
            viewHeight = 'xxl';
            tableHeight = '63vh';
            if (Array.isArray(selectedSnapshots) && selectedSnapshots.length > 0) {
                baselineSnapshot = selectedSnapshots[0] || null;
                currentSnapshot = selectedSnapshots[selectedSnapshots.length - 1] || null;
                penultimateSnapshot = selectedSnapshots.length > 1 ? selectedSnapshots[selectedSnapshots.length - 2] : null;
            }
            break;
        default:
            tableDescription = '';
            break;
    }

    const handleGeneratePDF = () => {
        if (source === 'bvt-table') {
            printBVTTablesToPDF(budgetData, selectedProject);
        } else if (source === 'payment-invoicing') {
            generateInvoicePDF(invoiceData, selectedProject);
        }
    };

    return (
        <Root>
            <IconButton onClick={handleClickOpen} sx={{ textAlign: 'right' }} size="large">
                <TableChartIcon />
            </IconButton>
            <Dialog open={openDialog} onClose={handleClose} maxWidth='xl' fullWidth sx={{ maxHeight: viewHeight }}>
                <IconButton
                    onClick={handleClose}
                    sx={{ color: '#000', position: 'absolute', top: '8px', right: '8px' }}
                    size="large">
                    {source === 'trade over/under' && (
                        <Button
                            variant="contained"
                            color="primary"
                            sx={{ marginRight: '8px' }}
                            href={`${window.location.origin}/ll/new-lesson`}
                            target="_blank"
                            className="lessons-learned-button"
                        >
                            Log Lessons Learned
                            <AddCircleIcon sx={{ paddingLeft: '2px', fontSize: 'medium', verticalAlign: 'middle' }} />
                        </Button>
                    )}
                    <CloseIcon sx={{color: 'white'}}/>
                </IconButton>
                <Box sx={{ padding: '15px', color: 'white', backgroundColor: '#093d61' }}>
                    <Grid container sx={{ alignItems: "center", justifyContent: "space-between" }}>
                        <Grid size={{ xs: 9 }}>
                            <Typography
                                className='titillium-web-base'
                                sx={{ fontSize: '25px', fontWeight: 'bolder' }}
                            >
                                {title}
                            </Typography>
                            <Typography className='titillium-web-base' sx={{ fontSize: 'small' }}>
                                {tableDescription}
                            </Typography>
                        </Grid>
                        <Grid size={{ xs: 3 }} container sx={{ justifyContent: "flex-end", alignItems: "center", paddingTop: '10px', marginTop: '10px' }} spacing={{ xs: 1 }} gap="2px">
                            {showButtons && (
                                <>
                                    {showButtons === 'bvt-table' && (
                                        <Button
                                            variant="contained"
                                            color="primary"
                                            className="pdf-button"
                                            onClick={handleGeneratePDF}
                                            sx={{ marginRight: '8px' }}
                                        >
                                            Download PDF
                                        </Button>
                                    )}
                                    {showButtons === 'payment-invoicing' && (
                                        <>
                                            <Button
                                                variant="contained"
                                                color="primary"
                                                className="pdf-button"
                                                onClick={handleGeneratePDF}
                                                sx={{ marginRight: '8px' }}
                                            >
                                                Download PDF
                                            </Button>
                                            <Button
                                                variant="contained"
                                                className="excel-button"
                                                onClick={() => generateExcel(invoiceData)}
                                                sx={{ marginRight: '8px' }}
                                            >
                                                Download Excel
                                            </Button>
                                        </>
                                    )}
                                </>
                            )}
                        </Grid>
                    </Grid>
                </Box>
                <DialogContent sx={{ 
                    height: tableHeight,
                    width: viewWidth,
                    display: 'flex', 
                    flexDirection: 'column', 
                    padding: '0px',
                }}>
                    <TableContainer sx={{ 
                        flex: 1, 
                        overflowY: (source === 'concur-table' || source === 'keystyle-table') ? 'hidden' : 'auto',
                    }}>
                        <TableComponent
                            budgetData={budgetData}
                            invoiceData={invoiceData}
                            selectedSnapshots={selectedSnapshots}
                            concurUrl={concurUrl}
                            isModal={true}
                            selectedProject={selectedProject}
                            firstSnapshot={firstSnapshot}
                            secondSnapshot={secondSnapshot}
                            setFirstSnapshot={setFirstSnapshot}
                            setSecondSnapshot={setSecondSnapshot}
                            projectId={projectId}
                        />
                    </TableContainer>
                </DialogContent>
                <DialogActions>
                    {source === 'invoice-portal' && (
                        <div style={{
                            width: '100%',
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center'
                        }}>
                            <Typography sx={{ color: 'black', fontSize: '14px', textAlign: 'center' }}>
                                This portal is managed by ABS. Please contact your project controller with any questions.
                            </Typography>
                        </div>
                    )}
                    <Button onClick={handleClose} color="primary">
                        Close
                    </Button>
                </DialogActions>
            </Dialog>
        </Root>
    );
});

export default TablePopoutButton;
